import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { PlusIcon } from "@pd/pd-ui-kit";
import { isEqual } from "lodash";
import {
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import useTemporaryAssignmentsSave from "~/components/Forms/UsersForm/hooks/useTemporaryAssignmentsSave";
import { useTemporaryAssignedDealers } from "~/components/Forms/UsersForm/hooks/useTemporaryAssignedDealers";
import {
  emptyRow,
  IAssignmentRow,
  normalizeForCompare,
  toDayKey,
} from "~/components/Forms/UsersForm/TemporaryAssignments/model/utils";
import { getTemporaryAssignmentsValidation } from "~/components/Forms/UsersForm/TemporaryAssignments/model/validation";

import AssignmentRow from "./AssignmentRow";

import s from "./TemporaryAssignments.less";

export interface ITemporaryAssignmentsRef {
  submit: () => Promise<void>;
}

export interface ITemporaryAssignmentsProps {
  userId: number;
  initialAssignments?: IAssignmentRow[];
  // Permanent-дилеры редактируемого пользователя — для исключения пересечений.
  permanentDealers?: { label: string; value: number }[];
  // Колбэк с актуальным списком дилеров, выбранных во временных строках —
  // нужен форме, чтобы исключить их из permanent-селекта (и наоборот).
  onDealersChange?: (dealerIds: number[]) => void;
}

const TemporaryAssignmentsComponent: ForwardRefRenderFunction<
  ITemporaryAssignmentsRef,
  ITemporaryAssignmentsProps
> = (
  { userId, initialAssignments, permanentDealers, onDealersChange },
  ref,
) => {
  const { t } = useTranslation();
  const { control, handleSubmit, reset, formState } = useForm<{
    assignments: IAssignmentRow[];
  }>({
    defaultValues: { assignments: [] },
    resolver: yupResolver(getTemporaryAssignmentsValidation(t)),
    mode: "onSubmit",
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "assignments",
  });
  const allAssignments = useWatch({ control, name: "assignments" }) as
    | IAssignmentRow[]
    | undefined;

  // Сообщаем форме актуальный набор дилеров во временных строках — она
  // исключает их из permanent-селекта (дилеры не должны пересекаться).
  // Эффект завязан на сериализованный ключ: allAssignments от useWatch
  // приходит новой ссылкой на каждый рендер, а onDealersChange кладёт
  // новый массив в state родителя — без ключа это бесконечный цикл.
  const dealerIdsKey = JSON.stringify(
    (allAssignments ?? []).map((row) => row?.dealers ?? []),
  );
  useEffect(() => {
    const ids: number[] = [];
    (allAssignments ?? []).forEach((row) => {
      (row?.dealers ?? []).forEach((id) => ids.push(id));
    });
    onDealersChange?.(ids);
  }, [dealerIdsKey, onDealersChange]);

  // Снимок исходных закреплений (с сервера) — для сравнения при сабмите.
  const initialNormalizedRef = useRef<ReturnType<typeof normalizeForCompare>>(
    [],
  );
  const permanentUserIdRef = useRef<number>(userId);
  permanentUserIdRef.current = userId;

  useEffect(() => {
    const init =
      initialAssignments && initialAssignments.length > 0
        ? initialAssignments
        : [];
    reset({ assignments: init });
    initialNormalizedRef.current = normalizeForCompare(init);
  }, [initialAssignments, reset]);

  const saveMutation = useTemporaryAssignmentsSave();

  // Дилеры, уже назначенные временно другим пользователям.
  // PASDILER-2078: исключение из селектов временно отключено в AssignmentRow,
  // чтобы бэк возвращал ошибку о дубле (сам набор по-прежнему считается).
  const externallyAssignedDealerIds = useTemporaryAssignedDealers(userId);

  const onValid = async (data: { assignments?: IAssignmentRow[] }) => {
    const rows = data.assignments ?? [];
    // Если закрепления не изменились относительно загруженных с сервера —
    // запрос не нужен (сравнение по нормализованным данным через isEqual).
    if (isEqual(normalizeForCompare(rows), initialNormalizedRef.current))
      return;

    // temporaryRmId (НА кого назначено) не шлём — сервер сам ставит userId.
    // permanentRmId (ОТНОСИТЕЛЬНО кого назначаем) по ТЗ необязателен — строки
    // без РМ («Не выбран») тоже отправляем; если бэк их отклоняет — ошибка бэка.
    const assignments = rows.map((row) => ({
      userId: permanentUserIdRef.current,
      dealerIds: row.dealers,
      assignmentType: "temporary" as const,
      permanentRmId: row.permanentRmId ?? undefined,
      // Даты шлём без времени (YYYY-MM-DD) по требованию бэка (PASDILER-2066).
      startDate: toDayKey(row.startDate) ?? undefined,
      endDate: toDayKey(row.endDate),
    }));

    await saveMutation.mutateAsync({
      userId: permanentUserIdRef.current,
      assignments,
    });
  };

  // Бросаем из onError, чтобы catch в UsersForm прервал закрытие модалки.
  const submitRef = useRef<() => Promise<void>>(() => Promise.resolve());
  submitRef.current = handleSubmit(onValid, () => {
    throw new Error("validation failed");
  });

  useImperativeHandle(ref, () => ({ submit: () => submitRef.current() }), []);

  return (
    <div className={s.container}>
      <div className={s.assignmentsBlock}>
        {fields.map((field, index) => (
          <AssignmentRow
            key={field.id}
            index={index}
            control={control}
            errors={formState.errors?.assignments?.[index]}
            userId={userId}
            permanentDealers={permanentDealers}
            allAssignments={allAssignments}
            externallyAssignedDealerIds={externallyAssignedDealerIds}
            onRemove={() => remove(index)}
          />
        ))}
      </div>
      <button
        type="button"
        className={s.addAssignment}
        onClick={() => append({ ...emptyRow, dealers: [] })}
      >
        <PlusIcon size={16} fill="#e37639" />
        {t("users.add_temporary_assignment")}
      </button>
    </div>
  );
};

export const TemporaryAssignments = forwardRef(TemporaryAssignmentsComponent);
export default TemporaryAssignments;
